import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import type { PublicQuestion } from 'shared';
import type { AnswerDraft } from '@/lib/exam-session';
import { cn } from '@/lib/utils';

export type QuestionCardProps = {
  question: PublicQuestion;
  index: number;
  total: number;
  draft: AnswerDraft | undefined;
  onChange: (next: AnswerDraft) => void;
};

const EMPTY: AnswerDraft = { selectedOptionIds: [], textAnswer: null, isFlagged: false };

export function QuestionCard({ question, index, total, draft, onChange }: QuestionCardProps) {
  const { t } = useTranslation();
  const current = draft ?? EMPTY;
  const isMultiple = question.type === 'multiple';

  const toggleOption = (optionId: string): void => {
    if (isMultiple) {
      const selected = current.selectedOptionIds.includes(optionId)
        ? current.selectedOptionIds.filter((id) => id !== optionId)
        : [...current.selectedOptionIds, optionId];
      onChange({ ...current, selectedOptionIds: selected });
      return;
    }
    onChange({ ...current, selectedOptionIds: [optionId] });
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-slate-500">
        {t('exam.questionProgress', { current: index + 1, total })}
      </p>

      <h2 className="mt-2 whitespace-pre-wrap text-lg leading-relaxed text-slate-900">
        {question.stem}
      </h2>

      {question.imageUrl && (
        <img
          src={question.imageUrl}
          alt={t('exam.questionImageAlt')}
          loading="lazy"
          className="mt-3 max-h-96 w-full rounded-lg border border-slate-200 object-contain"
        />
      )}

      {isMultiple && <p className="mt-2 text-sm text-slate-600">{t('exam.multipleHint')}</p>}

      {question.type === 'short_text' ? (
        <div className="mt-4">
          <label htmlFor={`answer-${question.id}`} className="sr-only">
            {question.stem}
          </label>
          <input
            id={`answer-${question.id}`}
            type="text"
            inputMode="text"
            autoComplete="off"
            value={current.textAnswer ?? ''}
            onChange={(event) => onChange({ ...current, textAnswer: event.target.value })}
            placeholder={t('exam.shortTextPlaceholder')}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base focus:border-slate-500"
          />
        </div>
      ) : (
        <fieldset className="mt-4">
          <legend className="sr-only">{question.stem}</legend>
          <div className="flex flex-col gap-2">
            {question.options.map((option) => {
              const checked = current.selectedOptionIds.includes(option.id);
              return (
                <label
                  key={option.id}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors',
                    checked
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-300 hover:border-slate-400',
                  )}
                >
                  <input
                    type={isMultiple ? 'checkbox' : 'radio'}
                    name={`question-${question.id}`}
                    value={option.id}
                    checked={checked}
                    onChange={() => toggleOption(option.id)}
                    className="mt-1 h-5 w-5 shrink-0 accent-slate-900"
                  />
                  <span className="text-base leading-relaxed text-slate-900">{option.label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      <button
        type="button"
        onClick={() => onChange({ ...current, isFlagged: !current.isFlagged })}
        aria-pressed={current.isFlagged}
        className={cn(
          'mt-4 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
          current.isFlagged
            ? 'border-amber-500 bg-amber-50 text-amber-800'
            : 'border-slate-300 text-slate-600 hover:bg-slate-50',
        )}
      >
        <Flag aria-hidden className="h-4 w-4" />
        {current.isFlagged ? t('exam.flagged') : t('exam.flag')}
      </button>
    </section>
  );
}
